import * as THREE from 'three';

const RING_OUTER = 30;
const RING_TUBE = 2.4;
const PARTICLE_COUNT = 320;

/**
 * Glowing red return-portal that appears in space when the player
 * arrives from a Vibejam webring hop (`?portal=true`). Sits at the
 * normal space spawn point so the player materialises right next to
 * it; flying through the disc returns them to the originating game
 * (`?ref=`).
 *
 * Design choices vs. the sample widget:
 *  - Larger ring (30u) so a moving ship registers a hit reliably.
 *  - Twin-ring + soft inner disc + slow particle swirl — reads as a
 *    portal in motion at distance, doesn't fight the sci-fi palette.
 *  - The label faces the player on every frame so it's legible even
 *    when the ring is rotated.
 */
export class SpaceStartPortal {
  readonly object3D: THREE.Group;
  readonly position: THREE.Vector3;
  readonly collisionRadius = RING_OUTER;

  private readonly inner: THREE.Mesh;
  private readonly innerMat: THREE.MeshBasicMaterial;
  private readonly outerRingMat: THREE.MeshBasicMaterial;
  private readonly haloMat: THREE.MeshBasicMaterial;
  private readonly labelMesh: THREE.Mesh;
  private readonly labelTexture: THREE.CanvasTexture;
  private readonly particleGeo: THREE.BufferGeometry;
  private readonly particleMat: THREE.PointsMaterial;
  private readonly particles: THREE.Points;
  private readonly disposables: Array<{
    geometry?: THREE.BufferGeometry;
    material?: THREE.Material | THREE.Material[];
  }> = [];
  private elapsed = 0;
  private disposed = false;

  constructor(position: THREE.Vector3) {
    this.position = position.clone();
    this.object3D = new THREE.Group();
    this.object3D.position.copy(this.position);

    // Outer torus — emissive cherry red with a very slight bloom
    // friendly intensity. Two layered rings give the silhouette a hint
    // of depth without needing post-processing.
    const ringGeo = new THREE.TorusGeometry(RING_OUTER, RING_TUBE, 14, 96);
    const ringMat = new THREE.MeshStandardMaterial({
      color: 0xff3a3a,
      emissive: 0xff5d5d,
      emissiveIntensity: 1.6,
      roughness: 0.3,
      metalness: 0.2
    });
    const ring = new THREE.Mesh(ringGeo, ringMat);
    this.object3D.add(ring);
    this.disposables.push({ geometry: ringGeo, material: ringMat });

    const innerRingGeo = new THREE.TorusGeometry(
      RING_OUTER - 1.6,
      RING_TUBE * 0.45,
      12,
      72
    );
    this.outerRingMat = new THREE.MeshBasicMaterial({
      color: 0xff8a8a,
      transparent: true,
      opacity: 0.85
    });
    const innerRing = new THREE.Mesh(innerRingGeo, this.outerRingMat);
    this.object3D.add(innerRing);
    this.disposables.push({ geometry: innerRingGeo, material: this.outerRingMat });

    // Soft translucent disc plugging the centre — the actual "go
    // back" surface. Subtle pulse animates the alpha.
    const innerGeo = new THREE.CircleGeometry(RING_OUTER - 3, 64);
    this.innerMat = new THREE.MeshBasicMaterial({
      color: 0xff5d5d,
      transparent: true,
      opacity: 0.42,
      side: THREE.DoubleSide,
      depthWrite: false
    });
    this.inner = new THREE.Mesh(innerGeo, this.innerMat);
    this.object3D.add(this.inner);
    this.disposables.push({ geometry: innerGeo, material: this.innerMat });

    // Outer halo — a wider disc, nearly transparent, that puts a soft
    // glow ring around the torus. Matches the mission marker palette.
    const haloGeo = new THREE.RingGeometry(RING_OUTER + 1, RING_OUTER + 7, 64);
    this.haloMat = new THREE.MeshBasicMaterial({
      color: 0xff5d5d,
      transparent: true,
      opacity: 0.18,
      side: THREE.DoubleSide,
      depthWrite: false
    });
    const halo = new THREE.Mesh(haloGeo, this.haloMat);
    this.object3D.add(halo);
    this.disposables.push({ geometry: haloGeo, material: this.haloMat });

    this.particleGeo = new THREE.BufferGeometry();
    const positions = new Float32Array(PARTICLE_COUNT * 3);
    for (let i = 0; i < PARTICLE_COUNT; i++) {
      const angle = Math.random() * Math.PI * 2;
      const radius = RING_OUTER + (Math.random() - 0.5) * 6;
      positions[i * 3 + 0] = Math.cos(angle) * radius;
      positions[i * 3 + 1] = Math.sin(angle) * radius;
      positions[i * 3 + 2] = (Math.random() - 0.5) * 5;
    }
    this.particleGeo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    this.particleMat = new THREE.PointsMaterial({
      color: 0xff8a8a,
      size: 0.45,
      transparent: true,
      opacity: 0.7,
      depthWrite: false
    });
    this.particles = new THREE.Points(this.particleGeo, this.particleMat);
    this.object3D.add(this.particles);
    this.disposables.push({ geometry: this.particleGeo, material: this.particleMat });

    // Floating label — a textured plane that we re-orient toward the
    // camera each frame for legibility.
    this.labelTexture = makeLabelTexture('RETURN PORTAL', '#ff8a8a');
    const labelGeo = new THREE.PlaneGeometry(34, 6);
    const labelMat = new THREE.MeshBasicMaterial({
      map: this.labelTexture,
      transparent: true,
      depthWrite: false,
      side: THREE.DoubleSide
    });
    this.labelMesh = new THREE.Mesh(labelGeo, labelMat);
    this.labelMesh.position.y = RING_OUTER + 9;
    this.object3D.add(this.labelMesh);
    this.disposables.push({ geometry: labelGeo, material: labelMat });
  }

  /** Tick idle animations + face the label toward the camera so the
   *  text stays readable from any approach angle. */
  update(dt: number, cameraPos: THREE.Vector3): void {
    if (this.disposed) return;
    this.elapsed += dt;
    const pulse = 0.5 + 0.5 * Math.sin(this.elapsed * 1.4);
    this.innerMat.opacity = 0.32 + 0.22 * pulse;
    this.outerRingMat.opacity = 0.65 + 0.25 * pulse;
    this.haloMat.opacity = 0.12 + 0.14 * pulse;

    // Slow particle swirl around the rim.
    const attr = this.particleGeo.getAttribute('position') as THREE.BufferAttribute;
    const positions = attr.array as Float32Array;
    const cos = Math.cos(dt * 0.8);
    const sin = Math.sin(dt * 0.8);
    for (let i = 0; i < PARTICLE_COUNT; i++) {
      const x = positions[i * 3 + 0]!;
      const y = positions[i * 3 + 1]!;
      const z = positions[i * 3 + 2]!;
      positions[i * 3 + 0] = x * cos - y * sin;
      positions[i * 3 + 1] = x * sin + y * cos;
      positions[i * 3 + 2] = z + Math.sin(this.elapsed * 1.6 + i) * 0.02;
    }
    attr.needsUpdate = true;

    // Billboard the label toward the camera.
    this.labelMesh.lookAt(cameraPos);
  }

  /** True when `point` is inside the portal capture radius. The check
   *  is a simple sphere because the portal sits free-floating in
   *  space — no need for a tight planar test. */
  containsPoint(point: THREE.Vector3): boolean {
    return this.position.distanceToSquared(point) <= this.collisionRadius * this.collisionRadius;
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    for (const d of this.disposables) {
      d.geometry?.dispose();
      if (Array.isArray(d.material)) {
        for (const m of d.material) m.dispose();
      } else {
        d.material?.dispose();
      }
    }
    this.labelTexture.dispose();
    this.disposables.length = 0;
    this.object3D.parent?.remove(this.object3D);
  }
}

function makeLabelTexture(text: string, color: string): THREE.CanvasTexture {
  const canvas = document.createElement('canvas');
  canvas.width = 1024;
  canvas.height = 192;
  const ctx = canvas.getContext('2d');
  if (ctx) {
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.font = 'bold 92px Inter, system-ui, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.shadowColor = color;
    ctx.shadowBlur = 28;
    ctx.fillStyle = color;
    ctx.fillText(text, canvas.width / 2, canvas.height / 2);
  }
  const tex = new THREE.CanvasTexture(canvas);
  tex.anisotropy = 4;
  return tex;
}
